/**
 * Session-expiry handling — the single implementation.
 *
 * WHY THIS FILE EXISTS
 * JWTs are issued with a 7-day expiry and there is no refresh endpoint. There
 * WAS a 401 interceptor, but it lived on the axios instance in utils/api.js,
 * which is imported by 9 of 60 client files. The other 51 import raw `axios`
 * and build headers by hand, so they never saw it.
 *
 * On day eight those 51 files received a bare 401, fell into their catch block,
 * logged to console and set no state. The page rendered EMPTY — no error, no
 * redirect, nothing to click. Reloading did not help; only logging out and back
 * in did. That is indistinguishable from "the site is randomly broken".
 *
 * Interceptors in axios are per-instance: registering on the global default
 * does NOT affect instances made with axios.create(). So utils/api.js keeps its
 * own registration, and both now call into this one function rather than
 * carrying two copies of the rule (lesson 34).
 */

/** Auth endpoints where a 401 means "wrong credentials", not "session over". */
const CREDENTIAL_PATHS = [
  '/auth/login',
  '/auth/register',
  '/auth/verify-2fa',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/resend-2fa',
  '/auth/resend-verification'
];

/** Pages that already ARE the destination — redirecting would loop. */
const LOGIN_PATHS = ['/admin/login', '/client/login', '/photographer/login'];

/**
 * Decide where an expired session should land.
 * Uses the CURRENT PATH rather than the stored user object, because the stored
 * user is exactly the thing we are about to discard, and the old implementation
 * defaulted an admin to /client/login when it failed to parse.
 */
export function loginPathFor(pathname) {
  if (pathname.startsWith('/admin')) return '/admin/login';
  if (pathname.startsWith('/photographer')) return '/photographer/login';
  return '/client/login';
}

/**
 * @param {object} error - an axios error
 * @returns {boolean} true if the session was expired and a redirect was issued
 */
export function handleAuthExpiry(error) {
  if (error?.response?.status !== 401) return false;

  const url = String(error?.config?.url || '');
  if (CREDENTIAL_PATHS.some(p => url.includes(p))) return false;

  // No token means this was never an authenticated request — a public endpoint
  // answering 401 is its own business and must not bounce the visitor.
  const hadToken = !!localStorage.getItem('token');
  if (!hadToken) return false;

  const here = window.location.pathname;
  if (LOGIN_PATHS.some(p => here.startsWith(p))) return false;

  localStorage.removeItem('token');
  localStorage.removeItem('user');

  // Preserve where they were so login can send them back. RequireClientAuth
  // already reads this key.
  try { localStorage.setItem('redirectAfterLogin', here + window.location.search); } catch (e) { /* private mode */ }

  window.location.href = loginPathFor(here);
  return true;
}

export default handleAuthExpiry;
